import React from "react";
import { Link } from "react-router-dom";
import { IndianRupee, CreditCard, AlertOctagon, TrendingUp } from "lucide-react";

const fmtINR = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

/**
 * Revenue Snapshot - compact 4-card strip for money-flow signals.
 * Smaller than the old 12-stat dump, focused on the metrics that matter
 * for cash-flow conversations: collected, pending, overdue, conversions.
 */
export default function RevenueSnapshot({ stats, loading }) {
  const cards = [
    {
      key: "collected",
      label: "Collected this month",
      value: fmtINR(stats?.collected_this_month),
      Icon: IndianRupee,
      tone: "growth",
      to: "/members",
    },
    {
      key: "pending",
      label: "Pending payments",
      value: stats?.pending_payments ?? 0,
      Icon: CreditCard,
      tone: "leads",
      to: "/members?view=pending",
    },
    {
      key: "overdue",
      label: "Overdue payments",
      value: stats?.overdue_payments ?? 0,
      Icon: AlertOctagon,
      tone: "risk",
      to: "/members?view=overdue",
    },
    {
      key: "conversions",
      label: "Conversions this month",
      value: stats?.conversions_month ?? 0,
      Icon: TrendingUp,
      tone: "growth",
      to: "/leads?status=converted",
    },
  ];

  const TONE = {
    growth: "text-growth bg-growth/10",
    leads: "text-leads bg-leads/10",
    risk: "text-risk bg-risk/10",
  };

  return (
    <section data-testid="revenue-snapshot">
      <div className="flex items-end justify-between mb-3">
        <h3 className="font-display text-lg font-bold text-foreground tracking-tight">Revenue</h3>
        <Link to="/members" className="text-xs font-semibold text-muted-foreground hover:text-foreground" data-testid="revenue-view-all">
          See all members
        </Link>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {cards.map(c => (
          <Link
            key={c.key}
            to={c.to}
            data-testid={`revenue-${c.key}`}
            className="group rounded-2xl border border-border bg-card p-4 hover:border-foreground/15 transition-all"
          >
            <div className="flex items-start justify-between mb-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground/80">
                {c.label}
              </p>
              <span className={`w-7 h-7 rounded-md flex items-center justify-center ${TONE[c.tone]}`}>
                <c.Icon className="w-3.5 h-3.5" strokeWidth={2.4} />
              </span>
            </div>
            <p className="font-display text-2xl md:text-3xl font-bold tracking-tight text-foreground" data-testid={`revenue-${c.key}-value`}>
              {loading ? "-" : c.value}
            </p>
          </Link>
        ))}
      </div>
    </section>
  );
}
